import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { ProFormSelect, ProFormTextArea } from "@ant-design/pro-components";
import { Button, message } from "antd";
import { PageEnum, TABLEPAI, ACTIVE } from "@/enums/pageEnum";
import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "@/store";
import { UserAndOrganFilter } from "@/utils/commonConfig";
import {
  BasicTable,
  HView,
  CustomDropdown,
  Status,
  CustomModal,
} from "@/components";
import { putSuspend, addOrganization, putResume, organType } from "@api";

export default function Organization() {
  const { pathname } = useLocation();
  const navigation = useNavigate();
  const { t } = useTranslation();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [refreshKey, setRefreshKey] = useState<number>();
  const [userTypeLists, setUserTypeLists] = useState<any[]>([]);
  const userName = useAppSelector((state) => state.app.userName);

  const puspend = async (id: number | string[]) => {
    try {
      await putSuspend(id);
      setSelectedRowKeys([]);
      setRefreshKey(Math.random());
    } catch (error: any) {
      message.error(error);
    }
  };

  const resume = async (id: number | string[]) => {
    await putResume(id);
    setRefreshKey(Math.random());
  };

  const getOrganType = async () => {
    const res = await organType();
    const data = res.data.body?.map((item: any) => ({
      value: item.type,
      label: t(item.type),
    }));
    setUserTypeLists(data || []);
  };

  const columns = useMemo(
    () => [
      { title: t("organization"), dataIndex: "name" },
      {
        title: t("organType"),
        dataIndex: "type",
        renderText: (text: string) => <>{t(text)}</>,
      },
      { title: t("admins"), dataIndex: "admins" },
      {
        title: t("status"),
        dataIndex: "status",
        renderText: (text: string) => <Status status={text} />,
      },
      {
        title: t("action"),
        dataIndex: "action",
        renderText: (_: string, record: any) => {
          const { id, status, name } = record;
          return (
            <>
              <HView
                name={t("view")}
                path={`${PageEnum.USERMANGMENTDETAL}/${id}`}
                state={record}
              />
              {name !== userName && (
                <CustomDropdown
                  dropdownRender={() => (
                    <div className="bg-[#fff] dark:bg-[#141414] cgboxShadow flex flex-col justify-center items-center">
                      <Button
                        color="default"
                        variant="link"
                        onClick={() => {
                          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                          status === ACTIVE ? puspend([id]) : resume([id]);
                        }}
                      >
                        {status === ACTIVE ? t("suspend") : t("resume")}
                      </Button>
                    </div>
                  )}
                />
              )}
            </>
          );
        },
      },
    ],
    [t, userName]
  );

  if (pathname !== PageEnum.ORGANIZATION) {
    return <Outlet />;
  }

  return (
    <div className="p-4">
      <BasicTable
        columns={columns}
        filterTableFields={UserAndOrganFilter(t("organization"), t("status"))}
        key={refreshKey}
        isDefaultToolBar={false}
        headerTitle={() => t("organList")}
        tableHeader={() => (
          <>
            <CustomModal
              title={t("addOrganiz")}
              request={addOrganization}
              onShowModal={() => getOrganType()}
              onClick={() => {
                setRefreshKey(Math.random());
              }}
            >
              <ProFormTextArea
                name="name"
                label={t("orgaName")}
                rules={[{ required: true }]}
                fieldProps={{ maxLength: 100, showCount: true }}
              />
              <ProFormSelect
                name="type"
                options={userTypeLists}
                label={t("type")}
                rules={[{ required: true }]}
              />
            </CustomModal>
            <Button
              style={{ marginLeft: 16, marginRight: 16 }}
              type="default"
              variant="outlined"
              onClick={() => {
                navigation(`${PageEnum.ORGANIZATION}/type-setting`);
              }}
            >
              {t("typeSetting")}
            </Button>
            <Button
              type="default"
              onClick={() => {
                puspend(selectedRowKeys as string[]);
              }}
              disabled={!selectedRowKeys.length}
            >
              {t("suspend")}
            </Button>
          </>
        )}
        requestUrl={TABLEPAI.ORGANIZA}
        onChangeSelected={(rowkeys) => setSelectedRowKeys(rowkeys)}
      />
    </div>
  );
}
